import {
  type GetPaginationSessionsParams,
  type GetSessionsParams,
  type SessionsOpportunity,
  useInfiniteScrollFromCache,
  useLazyGetSessionsOpportunitiesQuery,
} from '@carboncopyco/data';
import { Modal, TextSubHeader } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { FixedTable } from '../../../../FixedTable';
import { TableRowWrapper } from '../../../../InfoPage';
import { useDashboardProps } from '../../../DashboardContext';
import { MinimalTableSkeleton } from '../../../MinimalTable/MinimalTableSkeleton';
import { getCols } from './opportunitiesCols';

const cols = getCols(true);

export const ViewAllOpportunitiesModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    planId,
  } = useDashboardProps();

  const fetchArgs = useMemo<GetSessionsParams>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes:
        planId === 'environment'
          ? ['shared_presentation', 'managed_presentation', 'play']
          : ['managed_presentation', 'play'],
    }),
    [organizationId, short, internal, selectedWorkspaceIds, timeRange, planId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    SessionsOpportunity,
    GetPaginationSessionsParams
  >(useLazyGetSessionsOpportunitiesQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 pb-7">
          <TextSubHeader
            color="text-ds-natural-600"
            label="Opportunities"
          />
          {!!totalSize && (
            <TextSubHeader
              label={`(${totalSize})`}
              color="text-ds-natural-300"
            />
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-1">
        <FixedTable
          cols={cols}
          data={items}
          isLoading={isLoading}
          loader={
            <MinimalTableSkeleton
              rows={11}
              large
            />
          }
          isLoadingBatch={isLoadingBatch}
          handleFetchNextBatch={fetchNextBatch}
          error={error}
          rowWrapper={TableRowWrapper}
          infiniteListMaxHeight={600}
        />
      </div>
    </Modal>
  );
};
